import React, { PureComponent, Fragment } from 'react'
import styled from 'styled-components'

import withTransition from '../../hoc/Transition'
import withLoading from '../../hoc/Loading'
import { IWinnerPlayerData } from '../../helpers/data/winnerPlayer'

import isIosApp from '../../helpers/iosApp'

import Asset from '../../components/asset/Asset'
import { IAssetData } from '../../helpers/data/assetData'
import getFont from '../../helpers/fonts/fonts'
import lottieRendererEnums from '../../helpers/enums/lottieRenderer'

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	background-size: cover;
	position: relative;
`

const BackgroundContainer = styled.div`
	width: 100%;
	height: 100%;
	margin: auto;
	position: absolute;
	top: 0;
	left: 0;
`

const PodiumTitle = styled.div`
	width: 62%;
	height: 16%;
	margin: auto;
	position: absolute;
	right: 0;
	top: 13%;
	left: 0;
`
const PodiumTitleText = styled.h2`
	position: absolute;
	top: 38%;
	left: 50%;
	transform: translate(-50%, -50%);
	margin: 0;
	font-family: ${getFont('primaryHeavy')};
	font-size: clamp(1.2rem, 4vw, 2.5rem);
	color: #005785;
	text-align: center;
	z-index: 10;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	width: 100%;
`
const PodiumContainer = styled.div`
	width: 61%;
	height: 45%;
	margin: auto;
	position: absolute;
	right: 0;
	top: 44%;
	left: 0;
`
const PlayerContainer = styled.div`
	width: 20%;
	height: 33%;
	margin: auto;
	position: absolute;
	top: 22%;
`

const PlayerData = styled.div`
	width: 17%;
	height: 33%;
	color: white;
	font-size: 15%;
	position: absolute;
	text-align: center;
	white-space: nowrap;
`

const PlayerNickname = styled.div`
	width: 100%;
	font-family: ${getFont('primaryHeavy')};
	overflow: hidden;
	padding-bottom: 4%;
	text-overflow: ellipsis;
`

const PlayerPoints = styled.div`
	width: 100%;
	font-family: ${getFont('primaryMedium')};
`

const ScreenReaderOnly = styled.div`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`

export interface ISkin {
	background: IAssetData
	podium: IAssetData
	title: IAssetData
}

interface IPodiumProps {
	players: IWinnerPlayerData[]
	onViewLoaded: () => void
	skin: ISkin
}

class Podium extends PureComponent<IPodiumProps> {
	private animationTimeout = 0
	private loadCount = 0

	state = {
		loadedSteps: 0,
	}

	componentDidMount() {
		this.animationTimeout = window.setInterval(() => {
			if (this.state.loadedSteps > 3) {
				window.clearInterval(this.animationTimeout)
			} else {
				this.setState({
					loadedSteps: this.state.loadedSteps + 1,
				})
			}
		}, 250)
	}

	onImageLoaded = () => {
		this.loadCount += 1
		if (this.loadCount === 2) {
			this.props.onViewLoaded()
		}
	}

	getPlayerPositionString(player: IWinnerPlayerData, position: number) {
		const positionLabels = ['1st place', '2nd place', '3rd place']
		const label = positionLabels[position]
		return `${label}: ${player.nickname} ${player.characterName} ${player.score} points.`
	}

	render() {
		return (
			<Wrapper>
				<BackgroundContainer>
					<Asset onLoad={this.onImageLoaded} asset={this.props.skin.background} />
				</BackgroundContainer>
				<PodiumTitle>
				<Asset onLoad={this.onImageLoaded} asset={this.props.skin.title} ariaHidden />
				<PodiumTitleText>Congratulations!</PodiumTitleText>
				</PodiumTitle>
				{this.state.loadedSteps > 0 && (
					<PodiumContainer>
						<Asset asset={this.props.skin.podium} />
					</PodiumContainer>
				)}

				<ScreenReaderOnly aria-label="Podium results">
				{[
						this.props.players[0] && this.getPlayerPositionString(this.props.players[0], 0),
						this.props.players[1] && this.getPlayerPositionString(this.props.players[1], 1),
						this.props.players[2] && this.getPlayerPositionString(this.props.players[2], 2),
					]
					.filter(Boolean)
					.join(' ')}
				</ScreenReaderOnly>

				{this.props.players.length > 0 && this.state.loadedSteps > 1 && (
					<Fragment>
						<PlayerContainer
							style={{ top: '22.5%', left: '40%' }}
						>
							<Asset
								asset={this.props.players[0].podiumAsset}
								uid={this.props.players[0].playerUID + this.props.players[0].avatarId}
								renderer={lottieRendererEnums.CANVAS}
							/>
						</PlayerContainer>
						<PlayerData style={{ top: '70%', left: '41.5%' }} aria-hidden="true">
							<PlayerNickname>{this.props.players[0].nickname}</PlayerNickname>
							<PlayerPoints>{this.props.players[0].score} Points</PlayerPoints>
						</PlayerData>
					</Fragment>
				)}

				{this.props.players.length > 1 && this.state.loadedSteps > 2 && (
					<Fragment>
						<PlayerContainer
							style={{ top: '30.5%', left: isIosApp ? '22.5%' : '21%' }}
						>
							<Asset
								asset={this.props.players[1].podiumAsset}
								uid={this.props.players[1].playerUID + this.props.players[1].avatarId}
								renderer={lottieRendererEnums.CANVAS}
							/>
						</PlayerContainer>
						<PlayerData style={{ top: '77%', left: isIosApp ? '24%' : '22.5%' }} aria-hidden="true">
							<PlayerNickname>{this.props.players[1].nickname}</PlayerNickname>
							<PlayerPoints>{this.props.players[1].score} Points</PlayerPoints>
						</PlayerData>
					</Fragment>
				)}

				{this.props.players.length > 2 && this.state.loadedSteps > 3 && (
					<Fragment>
						<PlayerContainer
							style={{ top: '34.5%', left: isIosApp ? '57.5%' : '60%' }}
						>
							<Asset
								asset={this.props.players[2].podiumAsset}
								uid={this.props.players[2].playerUID + this.props.players[2].avatarId}
								renderer={lottieRendererEnums.CANVAS}
							/>
						</PlayerContainer>
						<PlayerData style={{ top: '80.5%', left: isIosApp ? '59%' : '61.5%' }} aria-hidden="true">
							<PlayerNickname>{this.props.players[2].nickname}</PlayerNickname>
							<PlayerPoints>{this.props.players[2].score} Points</PlayerPoints>
						</PlayerData>
					</Fragment>
				)}
			</Wrapper>
		)
	}

	componentWillUnmount() {
		window.clearInterval(this.animationTimeout)
	}
}

export default withTransition(withLoading(Podium))
